import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../context/AuthContext'
import Navbar from '../../components/Navbar'
import Footer from '../../components/Footer'

const CATEGORY_LABELS = {
  jersey: 'Jerseys',
  scarf: 'Scarves & Caps',
  badge: 'Badges & Stickers',
  other: 'Other Merchandise',
}

const CATEGORIES = ['all', 'jersey', 'scarf', 'badge', 'other']

// ── Cart sidebar ──────────────────────────────────────────────────────────────
function CartSidebar({ cart, onClose, onRemove, onUpdateQty, onCheckout, checkingOut, authLoading }) {
  const total = cart.reduce((sum, item) => sum + item.price * item.quantity, 0)

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="flex-1 bg-black/60" onClick={onClose} />
      <div className="w-full max-w-sm bg-white dark:bg-gray-950 border-l border-gray-200 dark:border-white/10 flex flex-col h-full">
        <div className="px-6 py-5 border-b border-gray-200 dark:border-white/10 flex justify-between items-center">
          <h2 className="font-black text-lg uppercase tracking-tight text-gray-900 dark:text-white">
            Your Cart ({cart.length})
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors text-xl">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {cart.length === 0 ? (
            <div className="text-gray-400 text-sm text-center py-16">Your cart is empty</div>
          ) : (
            <div className="flex flex-col gap-4">
              {cart.map(item => (
                <div key={item.cartKey} className="flex gap-3 border-b border-gray-100 dark:border-white/5 pb-4">
                  <div className="w-14 h-14 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 flex items-center justify-center shrink-0">
                    {item.image_url
                      ? <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                      : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-gray-400"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900 dark:text-white truncate">{item.name}</p>
                    {item.selectedSize && (
                      <p className="text-xs text-gray-400 mt-0.5">Size: <span className="font-bold text-gray-600 dark:text-gray-300">{item.selectedSize}</span></p>
                    )}
                    <p className="text-xs text-green-600 dark:text-green-400 font-bold mt-0.5">
                      ZMW {item.price.toFixed(2)}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <button onClick={() => onUpdateQty(item.cartKey, item.quantity - 1)}
                        className="w-6 h-6 border border-gray-200 dark:border-white/10 text-gray-500 hover:text-gray-900 dark:hover:text-white text-sm font-bold transition-colors flex items-center justify-center">−</button>
                      <span className="text-sm font-bold text-gray-900 dark:text-white w-6 text-center">{item.quantity}</span>
                      <button onClick={() => onUpdateQty(item.cartKey, item.quantity + 1)}
                        className="w-6 h-6 border border-gray-200 dark:border-white/10 text-gray-500 hover:text-gray-900 dark:hover:text-white text-sm font-bold transition-colors flex items-center justify-center">+</button>
                      <button onClick={() => onRemove(item.cartKey)}
                        className="ml-2 text-xs text-red-500 hover:text-red-400 font-bold uppercase tracking-widest transition-colors">Remove</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {cart.length > 0 && (
          <div className="px-6 py-5 border-t border-gray-200 dark:border-white/10">
            <div className="flex justify-between items-center mb-4">
              <span className="text-sm font-bold text-gray-500 uppercase tracking-widest">Total</span>
              <span className="text-xl font-black text-gray-900 dark:text-white">ZMW {total.toFixed(2)}</span>
            </div>
            <button
              onClick={onCheckout}
              disabled={checkingOut || authLoading}
              className="w-full bg-green-600 hover:bg-green-700 disabled:bg-green-900 disabled:text-green-700 text-white py-4 font-bold uppercase tracking-widest text-sm transition-colors"
            >
              {authLoading ? 'Loading...' : checkingOut ? 'Processing...' : 'Checkout'}
            </button>
            <p className="text-xs text-gray-400 text-center mt-2">Secure payment</p>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Product card ──────────────────────────────────────────────────────────────
function ProductCard({ product, onAddToCart }) {
  const hasSizes = product.sizes && product.sizes.length > 0
  const availableSizes = hasSizes ? product.sizes.filter(s => s.stock > 0) : []
  const [selectedSize, setSelectedSize] = useState(null)
  const [sizeError, setSizeError] = useState(false)

  const handleAdd = () => {
    if (hasSizes && !selectedSize) {
      setSizeError(true)
      setTimeout(() => setSizeError(false), 2000)
      return
    }
    onAddToCart(product, selectedSize)
    setSelectedSize(null)
  }

  const isSoldOut = hasSizes ? availableSizes.length === 0 : product.stock === 0

  return (
    <div className="bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-green-500/50 dark:hover:border-green-700/50 transition-all group flex flex-col">
      <div className="aspect-square bg-white dark:bg-white/5 border-b border-gray-200 dark:border-white/10 flex items-center justify-center overflow-hidden">
        {product.image_url ? (
          <img src={product.image_url} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        ) : (
          <div className="flex flex-col items-center gap-2 text-gray-300 dark:text-white/20">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
              <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
              <line x1="3" y1="6" x2="21" y2="6"/>
              <path d="M16 10a4 4 0 01-8 0"/>
            </svg>
            <span className="text-xs uppercase tracking-widest">No image</span>
          </div>
        )}
      </div>

      <div className="p-5 flex flex-col flex-1">
        <div className="text-xs text-gray-400 uppercase tracking-widest font-semibold mb-1">
          {CATEGORY_LABELS[product.category] || product.category}
        </div>
        <h3 className="font-black text-gray-900 dark:text-white text-sm uppercase tracking-tight mb-2 leading-tight flex-1">
          {product.name}
        </h3>
        {product.description && (
          <p className="text-xs text-gray-500 mb-3 leading-relaxed">{product.description}</p>
        )}

        {/* Size selector */}
        {hasSizes && (
          <div className="mb-3">
            <div className={`text-xs font-semibold uppercase tracking-widest mb-2 ${sizeError ? 'text-red-500' : 'text-gray-500'}`}>
              {sizeError ? 'Please select a size' : 'Select Size'}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {product.sizes.map(s => {
                const outOfStock = s.stock === 0
                const isSelected = selectedSize === s.size
                return (
                  <button
                    key={s.size}
                    type="button"
                    disabled={outOfStock}
                    onClick={() => setSelectedSize(s.size)}
                    className={`px-2.5 py-1 text-xs font-bold uppercase tracking-widest border transition-colors ${
                      outOfStock
                        ? 'border-gray-200 dark:border-white/5 text-gray-300 dark:text-white/20 cursor-not-allowed line-through'
                        : isSelected
                          ? 'bg-green-600 border-green-600 text-white'
                          : 'border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-400 hover:border-green-500 hover:text-green-600'
                    }`}
                  >
                    {s.size}
                  </button>
                )
              })}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-200 dark:border-white/10">
          <span className="text-lg font-black text-green-600 dark:text-green-400">
            ZMW {product.price.toFixed(2)}
          </span>
          <button
            onClick={handleAdd}
            disabled={isSoldOut}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors ${
              isSoldOut
                ? 'bg-gray-200 dark:bg-white/5 text-gray-400 cursor-not-allowed'
                : sizeError
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'bg-green-600 hover:bg-green-700 text-white'
            }`}
          >
            {isSoldOut ? 'Sold Out' : 'Add to Cart'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main shop page ────────────────────────────────────────────────────────────
export default function Shop() {
  const { user, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeCategory, setActiveCategory] = useState('all')

  const [cart, setCart] = useState(() => {
    try {
      const saved = localStorage.getItem('mwfc_cart')
      return saved ? JSON.parse(saved) : []
    } catch { return [] }
  })
  const [cartOpen, setCartOpen] = useState(false)
  const [checkingOut, setCheckingOut] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    try { localStorage.setItem('mwfc_cart', JSON.stringify(cart)) } catch {}
  }, [cart])

  useEffect(() => {
    supabase
      .from('products')
      .select('id, name, description, price, currency, category, image_url, stock, sizes')
      .eq('available', true)
      .order('category')
      .then(({ data }) => {
        if (data) setProducts(data)
        setLoading(false)
      })
  }, [])

  useEffect(() => {
    if (!authLoading && user && cart.length > 0) {
      const fromLogin = document.referrer.includes('/login')
      if (fromLogin) setCartOpen(true)
    }
  }, [authLoading, user, cart.length])

  const filtered = activeCategory === 'all'
    ? products
    : products.filter(p => p.category === activeCategory)

  // Cart key = productId + size (or just productId if no size)
  const addToCart = (product, selectedSize) => {
    const cartKey = selectedSize ? `${product.id}-${selectedSize}` : product.id
    setCart(prev => {
      const existing = prev.find(i => i.cartKey === cartKey)
      if (existing) return prev.map(i => i.cartKey === cartKey ? { ...i, quantity: i.quantity + 1 } : i)
      return [...prev, { ...product, cartKey, selectedSize: selectedSize || null, quantity: 1 }]
    })
    setCartOpen(true)
  }

  const removeFromCart = (cartKey) => setCart(prev => prev.filter(i => i.cartKey !== cartKey))

  const updateQty = (cartKey, qty) => {
    if (qty <= 0) { removeFromCart(cartKey); return }
    setCart(prev => prev.map(i => i.cartKey === cartKey ? { ...i, quantity: qty } : i))
  }

  const cartCount = cart.reduce((sum, i) => sum + i.quantity, 0)
  const cartTotal = cart.reduce((sum, i) => sum + i.price * i.quantity, 0)

  const handleCheckout = async () => {
    if (authLoading) return
    if (!user) { navigate('/login?redirect=/shop'); return }
    if (cart.length === 0) return

    setError(null)
    setCheckingOut(true)

    const orderItems = cart.map(i => ({
      product_id: i.id,
      name: i.name,
      price: i.price,
      quantity: i.quantity,
      size: i.selectedSize || null,
    }))

    const { data: order, error: orderError } = await supabase
      .from('orders')
      .insert({
        supporter_id: user.id,
        items: orderItems,
        total_amount: cartTotal,
        currency: 'ZMW',
        status: 'pending',
      })
      .select('id')
      .single()

    if (orderError) {
      setError('Failed to create order. Please try again.')
      setCheckingOut(false)
      return
    }

    // Payment gateway placeholder — integrate DPO Pay or Flutterwave here
    console.log('Order created:', order.id, '— payment gateway pending')
    setError('Payment gateway coming soon. Your order has been saved.')
    setCheckingOut(false)
  }

  return (
    <div className="min-h-screen bg-white dark:bg-black text-gray-900 dark:text-white flex flex-col">
      <Navbar />

      <div className="px-4 sm:px-8 py-10 sm:py-14 border-b border-gray-200 dark:border-white/10 bg-gradient-to-br from-green-950 to-black">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4">
          <div>
            <div className="text-xs text-green-400 uppercase tracking-widest font-semibold mb-2">Official Merchandise</div>
            <h1 className="text-3xl sm:text-5xl font-black uppercase tracking-tight text-white">MWFC Shop</h1>
            <p className="text-gray-400 text-sm mt-2">Represent the Wanderers. Official gear, delivered to you.</p>
          </div>
          <button
            onClick={() => setCartOpen(true)}
            className="relative flex items-center gap-2 border border-white/20 hover:border-green-500 text-white px-5 py-3 text-xs font-bold uppercase tracking-widest transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
              <line x1="3" y1="6" x2="21" y2="6"/>
              <path d="M16 10a4 4 0 01-8 0"/>
            </svg>
            Cart
            {cartCount > 0 && (
              <span className="absolute -top-2 -right-2 w-5 h-5 bg-green-600 text-white text-xs font-black flex items-center justify-center rounded-full">
                {cartCount}
              </span>
            )}
          </button>
        </div>
      </div>

      <div className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-8 py-8">
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-400 px-4 py-3 text-sm mb-6">{error}</div>
        )}

        <div className="flex gap-2 flex-wrap mb-8">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors border ${
                activeCategory === cat
                  ? 'bg-green-600 border-green-600 text-white'
                  : 'border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 hover:border-green-500 hover:text-green-600 dark:hover:text-green-400'
              }`}
            >
              {cat === 'all' ? 'All Products' : CATEGORY_LABELS[cat]}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-gray-400 text-sm text-center py-20">Loading products...</div>
        ) : filtered.length === 0 ? (
          <div className="text-gray-400 text-sm text-center py-20">No products in this category</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {filtered.map(product => (
              <ProductCard key={product.id} product={product} onAddToCart={addToCart} />
            ))}
          </div>
        )}

        {!authLoading && !user && cart.length > 0 && (
          <div className="mt-8 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <p className="text-sm text-green-700 dark:text-green-300">Sign in to complete your purchase.</p>
            <button
              onClick={() => navigate('/login?redirect=/shop')}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-2.5 text-xs font-bold uppercase tracking-widest transition-colors whitespace-nowrap"
            >
              Sign In to Checkout
            </button>
          </div>
        )}
      </div>

      <Footer />

      {cartOpen && (
        <CartSidebar
          cart={cart}
          onClose={() => setCartOpen(false)}
          onRemove={removeFromCart}
          onUpdateQty={updateQty}
          onCheckout={handleCheckout}
          checkingOut={checkingOut}
          authLoading={authLoading}
        />
      )}
    </div>
  )
}